<template>
  <notifications :speed="options.speed"
                 :duration="options.duration"
                 class="notifications-message"
                 position="bottom center"
                 animation-name="snackbar"
                 @click.native="onClick"
  />
</template>

<script>
/**
 *  Component in charged of displaying messages.
 *  Listen to the following events from the eventBus:
 *   - message-info
 *   - message-success
 *   - message-warning
 *   - message-error
 *   - request-failed
 *   - request-async-failed
 *
 *  Also available in Vue instance with the following methods
 *   - this.$message.info(message)
 *   - this.$message.success(message)
 *   - this.$message.warning(message)
 *   - this.$message.error(message)
 */
import { isUndefined, each, upperFirst } from 'lodash-es'
import Vue from 'vue'
import eventBus from '@/modules/eventBus'

export default {
  data () {
    return {
      messages: [],
      // Boolean to know if a message is currently displayed
      isDisplayingMessage: false,
      // Timer to display next message
      displayTimer: null
    }
  },

  computed: {
    options () {
      return {
        // Animation duration
        speed: 300,
        // Time to display message
        duration: 5000
      }
    }
  },

  watch: {
    messages (messages) {
      this.displayMessage()
    }
  },

  created () {
    Vue.prototype.$message = {
      info: (message, details) => { eventBus.$emit('message-info', message, details) },
      success: (message, details) => { eventBus.$emit('message-success', message, details) },
      warning: (message, details) => { eventBus.$emit('message-warning', message, details) },
      error: (message, details) => { eventBus.$emit('message-error', message, details) }
    }

    // generic listeners
    eventBus.$on('message-info', this.addInfoMessage)
    eventBus.$on('message-success', this.addSuccessMessage)
    eventBus.$on('message-warning', this.addWarningMessage)
    eventBus.$on('message-error', this.addErrorMessage)

    // request listeners
    eventBus.$on('request-failed', this.addRequestFailedMessage)
    eventBus.$on('request-async-failed', this.addRequestAsyncFailedMessage)
  },

  destroyed () {
    // generic listeners
    eventBus.$off('message-info', this.addInfoMessage)
    eventBus.$off('message-success', this.addSuccessMessage)
    eventBus.$off('message-warning', this.addWarningMessage)
    eventBus.$off('message-error', this.addErrorMessage)

    // request listeners
    eventBus.$off('request-failed', this.addRequestFailedMessage)
    eventBus.$off('request-async-failed', this.addRequestAsyncFailedMessage)
  },

  methods: {
    // Display an info message
    addInfoMessage (message, details) {
      this.addMessage({ type: 'info', message, details })
    },

    // Display a success message
    addSuccessMessage (message, details) {
      this.addMessage({ type: 'success', message, details })
    },

    // Display a warning message
    addWarningMessage (message, details) {
      this.addMessage({ type: 'warning', message, details })
    },

    // Display an error message
    addErrorMessage (message, details) {
      this.addMessage({ type: 'error', message, details })
    },

    // Display a message
    addMessage (messageObj) {
      const msgObj = this.formatMessage(messageObj)
      // Add in queue to display it
      this.messages.push(msgObj)
      // And store it in the notification store
      this.$store.commit('addNotification', msgObj)
    },

    // Reset all messages
    resetMessages () {
      this.messages = []
    },

    getRequestFailedMessage (errorData) {
      let errorMessage = ''

      if (!isUndefined(errorData.code)) {
        if (/\d+/.test(errorData.code)) { // code are digits => LNS errors => display message as is
          errorMessage += this.$t('messages:lnsMessage', { error: errorData.message })
        } else if (i18next.exists('serverError:' + errorData.code)) {
          errorMessage += this.$t('serverError:' + errorData.code, errorData.params)
        } else if (errorData.message) {
          errorMessage += errorData.message
        }
      }

      if (errorData.errors && errorData.errors.length > 0) {
        each(errorData.errors, error => {
          errorMessage += ' ' + this.getRequestFailedMessage(error)
        })
      }

      return errorMessage
    },

    addRequestFailedMessage (thrown, config) {
      if (
        !config ||
        !config.options ||
        isUndefined(config.options.defaultErrorMessage) || config.options.defaultErrorMessage
      ) {
        let serverError = this.$t('messages:serverError')
        if (thrown.response) serverError = this.getRequestFailedMessage(thrown.response.data)
        else serverError = this.$t('messages:timeout')
        this.addErrorMessage(serverError, config)
      }
    },

    addRequestAsyncFailedMessage (thrown, config) {
      if (
        !config ||
        !config.options ||
        isUndefined(config.options.defaultErrorMessage) || config.options.defaultErrorMessage
      ) {
        if (thrown.isTimeout) {
          if (thrown.command) {
            this.addWarningMessage(this.$t('messages:asyncTimeoutCmd', { command: thrown.command }))
          } else {
            this.addWarningMessage(this.$t('messages:asyncTimeout'), config)
          }
        } else if (thrown.isCancel) {
          this.addWarningMessage(thrown.reason, config)
        } else if (thrown.data && thrown.data.status === 'KO') {
          this.addWarningMessage(thrown.data.errorMessage, config)
        }
      }
    },

    /**
     *  Format messages to be displayed and store with the same object
     *  Two types of messages are supported:
     *  <strong>From client</strong>: {type: <the message type>, message: <the message>}
     *  <strong>From server</strong>: {status: <the response status>, data: {message: <the message>}}
     *  The message types are based on the HTTP code status:<br>
     *    <ul>
     *      <li>200 => 299 : success (disapear after 5 seconds)</li>
     *      <li>400 => 599 : error</li>
     *      <li>others : info  (disapear after 5 seconds)</li>
     *    </ul>
     */
    formatMessage (messageObj) {
      // Get message type
      let messageType = messageObj.type

      if (messageObj.status) {
        // Server message
        // Get message type depending on the HTTP message status
        const status = messageObj.status
        if (status >= 200 && status < 300) {
          messageType = 'success'
        } else if (status >= 400 && status < 600) {
          messageType = 'error'
        } else {
          messageType = 'info'
        }
      }

      // If no type detected, quit
      if (!messageType) {
        console.warn('[Messages] Undefined message type.')
      }

      // Get message
      let messageText = messageObj.message

      if (messageObj.status) {
        // Server message
        // Get message text fom data
        messageText = messageObj.data.message
      }

      // Set default message in case of empty error message
      if (messageText === '') {
        messageText = this.$t('messages:serverError')
      }

      return {
        date: Date.now(),
        type: messageType,
        message: messageText,
        details: messageObj.details
      }
    },

    /**
     *  Display messages (one at a time) when messages list changed.<br>
     *  The messages list is cleared after messages displayed.
     */
    displayMessage () {
      // Nothing to do for empty messages list or if a message is already displayed
      if (this.messages.length > 0 && !this.isDisplayingMessage) {
        // Get first message of the queue
        const msg = this.messages[0]

        // Display the message
        this.$notify({
          type: msg.type,
          text: upperFirst(msg.message)
        })
        this.isDisplayingMessage = true

        // No hide callback in vue-notification, we need to calculate the time in DOM
        // Add 3 speed for enter, move and leave animations
        // Add 100ms to prevent some setTimeout overflow
        const timeOnAir = this.options.speed * 3 + this.options.duration + 100

        // Remove this message from queue when hidden, to trigger next message
        this.displayTimer = setTimeout(() => {
          this.isDisplayingMessage = false
          this.messages.shift()
        }, timeOnAir)
      }
    },

    // Hide message on click (native behavior) and remove it from queue
    onClick () {
      clearTimeout(this.displayTimer)

      // Small timer to let the previous message to be closed
      setTimeout(() => {
        this.isDisplayingMessage = false
        this.messages.shift()
      }, 350)
    }
  }
}
</script>

<style lang="scss">
  @import "~@/scss/variables";

  $notification-height: 51px;

  .notifications.notifications-message {
    bottom: 0;
    left: 0;
    width: 100%;
    pointer-events: none;

    /* Use of !important to override inline calculated style by vue-notification */
    @media (min-width: $grid-breakpoint-md) {
      left: $sidebar-width !important;
      width: calc(100% - #{$sidebar-width}) !important;
    }

    .notification-wrapper {
      position: relative;
      width: 100%;
      margin: 0 auto;
      pointer-events: all;
      cursor: pointer;

      @media (min-width: $grid-breakpoint-sm) {
        width: calc(100% - 100px) !important;
      }

      @media (min-width: $grid-breakpoint-md) {
        width: calc(100% - 300px) !important;
      }

      @media (min-width: $grid-breakpoint-xl) {
        width: calc(100% - 500px) !important;
      }

      .vue-notification {
        padding: 15px;
        margin: 0;
        font-size: 0.9rem;
        border-left: none;
        border-radius: $border-radius;

        // Colors
        &.success {
          background-color: $success;
          background-image: none !important;
        }

        &.info {
          background-color: $info;
          background-image: none !important;
        }

        &.warning {
          background-color: $warning;
          background-image: none !important;
        }

        &.error {
          background-color: $danger;
          background-image: none !important;
        }

        // Link in messages
        .notification-content a {
          text-decoration: underline;
        }
      }
    }
  }

  .snackbar-enter,
  .snackbar-leave-to {
    bottom: -$notification-height;
  }

  .snackbar-enter-active,
  .snackbar-leave-active,
  .snackbar-move {
    transition: all 0.3s;
  }

  .snackbar-enter-to,
  .snackbar-leave {
    bottom: 0;
  }
</style>
